/**
 * Headless mode (-p) — pipe-friendly coding agent execution
 *
 * Writes Agent output directly to stdout (raw, no chalk wrapping).
 * Status, errors, and diff go to stderr.
 * Supports --output-format json for structured machine-readable output.
 * Designed for scripting: `spacecode -p "prompt" -R repo > output.txt`
 */

import type { ProxyMode, HeadlessResult } from '@spacecode/shared/remote';
import { validateRepo, validateBranch, validatePrompt } from '../utils/validation.js';
import { generateTaskId } from '../utils/task.js';
import { setupRun, coreExec, warnIgnoredOptions } from '../utils/run-setup.js';
import { captureException, logError } from '../posthog.js';
import { buildRemoteTask, statusFromResult, errorTaskStatus } from '../utils/remote-task.js';

export type OutputFormat = 'text' | 'json';

export interface HeadlessOptions {
  repo: string;
  prompt: string;
  branch?: string;
  noCommit?: boolean;
  idleTimeout?: number;
  proxyMode?: ProxyMode;
  outputFormat?: OutputFormat;
  verbose?: boolean;
}

export async function headless(options: HeadlessOptions): Promise<void> {
  const taskId = generateTaskId();
  const validRepo = validateRepo(options.repo);
  const validPrompt = validatePrompt(options.prompt);
  const validBranch = options.branch ? validateBranch(options.branch) : undefined;
  const startTime = Date.now();
  warnIgnoredOptions(options);

  // SIGINT → abort the request
  const abortController = new AbortController();
  let abortReason: 'sigint' | null = null;
  const sigintHandler = () => {
    abortReason = 'sigint';
    abortController.abort();
  };
  process.on('SIGINT', sigintHandler);

  const jsonMode = options.outputFormat === 'json';
  let finalExitCode = 1;

  // JSON mode: buffer output, emit structured object at the end
  const outputChunks: string[] = [];
  let resultDiff = '';

  try {
    const { envVars } = await setupRun({ proxyMode: options.proxyMode });

    let exitCode = 0;
    let success = true;

    // The core answers when the run is done: print its logs and diff, or buffer them for JSON mode.
    const viaCore = await coreExec({ repo: validRepo, prompt: validPrompt, envVars, branch: validBranch }, abortController.signal);
    exitCode = viaCore.exitCode;
    success = viaCore.success;
    resultDiff = viaCore.diff;
    if (jsonMode) {
      outputChunks.push(viaCore.logs);
    } else {
      if (viaCore.logs) process.stdout.write(viaCore.logs.endsWith('\n') ? viaCore.logs : viaCore.logs + '\n');
      if (viaCore.diff) process.stderr.write(viaCore.diff + '\n');
    }

    // JSON mode: emit structured result to stdout
    if (jsonMode) {
      const result: HeadlessResult = {
        taskId,
        repo: validRepo,
        branch: validBranch,
        result: outputChunks.join(''),
        exitCode,
        success,
        diff: resultDiff,
        durationSeconds: Math.round((Date.now() - startTime) / 1000),
      };
      process.stdout.write(JSON.stringify(result, null, 2) + '\n');
    }

    // Save task record (non-fatal — don't let save failure turn success into error)
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'headless',
        status: statusFromResult(success),
        exitCode,
        branch: validBranch,
      });
      await addTask(task);
    } catch (saveErr) {
      process.stderr.write(`warning: failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}\n`);
    }

    finalExitCode = exitCode;
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError';
    const isSigint = isAbort && abortReason === 'sigint';

    const errorExitCode = isSigint ? 130 : 1;
    const errorMessage = isSigint
      ? 'Cancelled by user'
      : (error instanceof Error ? error.message : String(error));
    const taskStatus = errorTaskStatus(isSigint);

    // JSON mode: emit error result
    if (jsonMode) {
      const result: HeadlessResult = {
        taskId,
        repo: validRepo,
        branch: validBranch,
        result: outputChunks.join(''),
        exitCode: errorExitCode,
        success: false,
        diff: resultDiff,
        durationSeconds: Math.round((Date.now() - startTime) / 1000),
        error: errorMessage,
      };
      process.stdout.write(JSON.stringify(result, null, 2) + '\n');
    }

    // Save task record
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'headless',
        status: taskStatus,
        error: errorMessage,
      });
      await addTask(task);
    } catch (saveErr) {
      process.stderr.write(`warning: failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}\n`);
    }

    if (!jsonMode && !isAbort) {
      process.stderr.write(`error: ${errorMessage}\n`);
    }

    // Report non-abort errors to Sentry
    if (!isAbort && error instanceof Error) {
      captureException(error, { command: 'headless', repo: validRepo, taskId });
      logError('headless task failed', { taskId, repo: validRepo, error: errorMessage });
    }

    finalExitCode = errorExitCode;
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }

  process.exit(finalExitCode);
}
